import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { environment } from '../../../../../environments/environment';
import { currentUser } from '../../../../core/auth';
import { AppState } from '../../../../core/reducers';
import { QueryParamsModel, QueryResultsModel } from '../../../../core/_base/crud';
import { Synonym, SynonymValue } from '../models';

@Injectable({ providedIn: 'root' })
export class SynonymService {
  private companyId;
  private baseUrl = environment.FORMS_CONCEPTS_BASE;

  constructor(private http: HttpClient, private store: Store<AppState>) {
    this.store.select(currentUser).subscribe((user) => (this.companyId = user.companyID));
  }

  private static getSynonymPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.newRequest) fromString += `&new_request=${filter.newRequest}`;
    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;
    if (filter.companyId) fromString += `&company_id=0&company_id=${filter.companyId}`;
    if (filter.conceptId) fromString += `&concept_id=${filter.conceptId}`;
    if (filter.dictionaryId) fromString += `&dictionary_id=${filter.dictionaryId}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.code) fromString += `&code_has=${filter.code}`;
      if (filter.description) fromString += `&description_has=${filter.description}`;
      if (filter.conceptCode) fromString += `&knostos_code_has=${filter.conceptCode}`;
    }

    return fromString;
  }

  private static getSynonymValuePageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `synonym_id=${filter.synonymId}&page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.value) fromString += `&value_has=${filter.value}`;
      if (filter.serial) fromString += `&serial_has=${filter.serial}`;
    }

    return fromString;
  }

  getSynonymPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: SynonymService.getSynonymPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(`${this.baseUrl}/dictionaries/synonyms`, { params });
  }

  findSynonymsAutocomplete(
    dictionaryId: number,
    searchTerm: string,
  ): Observable<QueryResultsModel> {
    const queryParams = new QueryParamsModel({ dictionaryId, searchTerm }, 'asc', 'code', 0, 25);
    const params = new HttpParams({
      fromString: SynonymService.getSynonymPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(`${this.baseUrl}/dictionaries/synonyms/autocomplete`, {
      params,
    });
  }

  createSynonym(synonym: Synonym): Observable<HttpResponse<{ data: Synonym }>> {
    return this.http.post<{ data: Synonym }>(
      synonym.company_id
        ? `${this.baseUrl}/dictionaries/synonyms/request`
        : `${this.baseUrl}/dictionaries/synonyms`,
      synonym,
      { observe: 'response' },
    );
  }

  updateSynonym(synonym: Synonym): Observable<HttpResponse<{ data: Synonym }>> {
    return this.http.put<{ data: Synonym }>(
      `${this.baseUrl}/dictionaries/synonyms/${synonym.id}${synonym.review ? '?review=true' : ''}`,
      synonym,
      {
        observe: 'response',
      },
    );
  }

  getSynonymValuePage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: SynonymService.getSynonymValuePageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(`${this.baseUrl}/dictionaries/synonyms/values`, {
      params,
    });
  }

  createSynonymValue(synonymValue: SynonymValue): Observable<HttpResponse<{ data: SynonymValue }>> {
    return this.http.post<{ data: SynonymValue }>(
      `${this.baseUrl}/dictionaries/synonyms/values`,
      synonymValue,
      { observe: 'response' },
    );
  }

  updateSynonymValue(synonymValue: SynonymValue): Observable<HttpResponse<{ data: SynonymValue }>> {
    return this.http.put<{ data: SynonymValue }>(
      `${this.baseUrl}/dictionaries/synonyms/values/${synonymValue.id}`,
      synonymValue,
      {
        observe: 'response',
      },
    );
  }

  deleteSynonymValue(synonymValueId: number): Observable<HttpResponse<void>> {
    return this.http.delete<void>(
      `${this.baseUrl}/dictionaries/synonyms/values/${synonymValueId}`,
      { observe: 'response' },
    );
  }
}
